import { A } from '@solidjs/router';
import { type Component, createResource, createSignal, For, type JSX, Show, Suspense } from 'solid-js';
import {
  type ArtifactWithProject,
  type ExecutionWithDetails,
  fetchDashboardMetrics,
  fetchExecutions,
  fetchMyArtifacts,
} from '../services/api.ts';

const Dashboard: Component = () => {
  // Metrics
  const [metrics] = createResource(fetchDashboardMetrics);

  // Paged data with load more
  const [executionOffset, setExecutionOffset] = createSignal(0);
  const [artifactOffset, setArtifactOffset] = createSignal(0);

  const pageSize = 5;

  // Fetch executions
  const [executions, { refetch: refetchExecutions }] = createResource(
    () => ({ limit: pageSize, offset: executionOffset() }),
    (params) => fetchExecutions(params),
  );

  // Fetch artifacts
  const [artifacts, { refetch: refetchArtifacts }] = createResource(
    () => ({ limit: pageSize, offset: artifactOffset() }),
    (params) => fetchMyArtifacts(params),
  );

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Format relative time
  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Execution status badge
  const StatusBadge: Component<{ status: string }> = (props) => {
    const statusConfig: Record<string, { color: string; icon: string; label: string }> = {
      running: { color: 'text-green-400 bg-green-400/10', icon: '●', label: 'Running' },
      completed: { color: 'text-blue-400 bg-blue-400/10', icon: '✓', label: 'Completed' },
      failed: { color: 'text-red-400 bg-red-400/10', icon: '✕', label: 'Failed' },
      pending: { color: 'text-yellow-400 bg-yellow-400/10', icon: '○', label: 'Pending' },
      cancelled: { color: 'text-gray-400 bg-gray-400/10', icon: '—', label: 'Cancelled' },
    };
    const config = statusConfig[props.status] || statusConfig.pending;
    return (
      <span
        class={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}
        title={config.label}
      >
        <span class={props.status === 'running' ? 'animate-pulse' : ''}>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  return (
    <div class="space-y-8">
      <h1 class="text-2xl font-medium">Dashboard</h1>

      {/* Metrics Cards */}
      <Suspense
        fallback={
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <For each={[1, 2, 3, 4]}>
              {() => (
                <div class="bg-gray-800/50 rounded-xl p-5 border border-gray-700 animate-pulse">
                  <div class="h-4 bg-gray-700 rounded w-20 mb-3" />
                  <div class="h-8 bg-gray-700 rounded w-16" />
                </div>
              )}
            </For>
          </div>
        }
      >
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Running Agents"
            value={metrics()?.runningAgents ?? 0}
            icon={
              <div class="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <span class={`text-green-400 text-lg ${(metrics()?.runningAgents ?? 0) > 0 ? 'animate-pulse' : ''}`}>●</span>
              </div>
            }
            highlight={(metrics()?.runningAgents ?? 0) > 0}
          />
          <MetricCard
            label="Cost (MTD)"
            value={formatCurrency(metrics()?.costMtd ?? 0)}
            icon={
              <div class="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <span class="text-purple-400 text-lg">$</span>
              </div>
            }
          />
          <MetricCard
            label="Views (MTD)"
            value={metrics()?.viewsMtd?.toLocaleString() ?? '0'}
            icon={
              <div class="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
            }
          />
          <MetricCard
            label="Earned"
            value={formatCurrency(metrics()?.totalEarnings ?? 0)}
            icon={
              <div class="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <span class="text-amber-400 text-lg">$</span>
              </div>
            }
          />
        </div>
      </Suspense>

      {/* Recent Executions */}
      <section class="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 class="text-lg font-medium">Recent Executions</h2>
        </div>
        <Suspense
          fallback={
            <div class="p-5">
              <div class="animate-pulse space-y-3">
                <div class="h-10 bg-gray-700 rounded" />
                <div class="h-10 bg-gray-700 rounded" />
                <div class="h-10 bg-gray-700 rounded" />
              </div>
            </div>
          }
        >
          <Show
            when={executions()?.items?.length}
            fallback={
              <div class="p-8 text-center text-gray-400">
                <p>No executions yet. Run an agent to see activity here.</p>
              </div>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-900/50">
                  <tr class="text-left text-sm text-gray-400">
                    <th class="px-5 py-3 font-medium">Status</th>
                    <th class="px-5 py-3 font-medium">Agent</th>
                    <th class="px-5 py-3 font-medium">Project</th>
                    <th class="px-5 py-3 font-medium">Duration</th>
                    <th class="px-5 py-3 font-medium">Cost</th>
                    <th class="px-5 py-3 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-700">
                  <For each={executions()?.items}>
                    {(execution) => (
                      <tr class="hover:bg-gray-700/30 transition-colors">
                        <td class="px-5 py-3">
                          <StatusBadge status={execution.status} />
                        </td>
                        <td class="px-5 py-3 text-sm">
                          {(execution as ExecutionWithDetails).agent_name ||
                            execution.agent_id?.slice(0, 8) ||
                            '-'}
                        </td>
                        <td class="px-5 py-3 text-sm">
                          <Show when={execution.project_id}>
                            <A
                              href={`/projects/${execution.project_id}`}
                              class="text-purple-400 hover:text-purple-300"
                            >
                              {(execution as ExecutionWithDetails).project_name ||
                                execution.project_id?.slice(0, 8)}
                            </A>
                          </Show>
                          <Show when={!execution.project_id}>
                            <span class="text-gray-500">-</span>
                          </Show>
                        </td>
                        <td class="px-5 py-3 text-sm text-gray-300">
                          <Show when={execution.status === 'running'}>
                            <span class="text-green-400">running...</span>
                          </Show>
                          <Show when={execution.status !== 'running'}>
                            {execution.completed_at && execution.started_at
                              ? `${((new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime()) / 1000).toFixed(1)}s`
                              : '-'}
                          </Show>
                        </td>
                        <td class="px-5 py-3 text-sm text-gray-300">
                          {execution.compute_cost ? formatCurrency(execution.compute_cost) : '-'}
                        </td>
                        <td class="px-5 py-3 text-sm text-gray-400">
                          {formatRelativeTime(execution.created_at)}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
            <Show when={executions()?.total && executions()!.total > (executionOffset() + pageSize)}>
              <div class="px-5 py-3 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setExecutionOffset((prev) => prev + pageSize);
                    refetchExecutions();
                  }}
                  class="text-sm text-purple-400 hover:text-purple-300"
                >
                  Load more...
                </button>
              </div>
            </Show>
          </Show>
        </Suspense>
      </section>

      {/* All Artifacts */}
      <section class="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 class="text-lg font-medium">All Artifacts</h2>
        </div>
        <Suspense
          fallback={
            <div class="p-5">
              <div class="animate-pulse space-y-3">
                <div class="h-10 bg-gray-700 rounded" />
                <div class="h-10 bg-gray-700 rounded" />
              </div>
            </div>
          }
        >
          <Show
            when={artifacts()?.items?.length}
            fallback={
              <div class="p-8 text-center text-gray-400">
                <p>No artifacts yet. Artifacts are created when agents run.</p>
              </div>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead class="bg-gray-900/50">
                  <tr class="text-left text-sm text-gray-400">
                    <th class="px-5 py-3 font-medium">Title</th>
                    <th class="px-5 py-3 font-medium">Project</th>
                    <th class="px-5 py-3 font-medium">Type</th>
                    <th class="px-5 py-3 font-medium">Views</th>
                    <th class="px-5 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-700">
                  <For each={artifacts()?.items}>
                    {(artifact) => (
                      <tr class="hover:bg-gray-700/30 transition-colors">
                        <td class="px-5 py-3">
                          <div class="font-medium text-white">{artifact.title}</div>
                          <div class="text-xs text-gray-400">
                            {artifact.description?.slice(0, 50)}
                          </div>
                        </td>
                        <td class="px-5 py-3 text-sm">
                          <Show when={artifact.projectId}>
                            <A
                              href={`/projects/${artifact.projectId}`}
                              class="text-purple-400 hover:text-purple-300"
                            >
                              {(artifact as ArtifactWithProject).projectName || 'View Project'}
                            </A>
                          </Show>
                          <Show when={!artifact.projectId}>
                            <span class="text-gray-500">-</span>
                          </Show>
                        </td>
                        <td class="px-5 py-3 text-sm">
                          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-blue-400 bg-blue-400/10">
                            {artifact.type}
                          </span>
                        </td>
                        <td class="px-5 py-3 text-sm text-gray-300">
                          {artifact.viewCount?.toLocaleString() || '0'}
                        </td>
                        <td class="px-5 py-3 text-sm text-gray-400">
                          {formatRelativeTime(artifact.createdAt)}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
            <Show when={artifacts()?.total && artifacts()!.total > (artifactOffset() + pageSize)}>
              <div class="px-5 py-3 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setArtifactOffset((prev) => prev + pageSize);
                    refetchArtifacts();
                  }}
                  class="text-sm text-purple-400 hover:text-purple-300"
                >
                  Load more...
                </button>
              </div>
            </Show>
          </Show>
        </Suspense>
      </section>
    </div>
  );
};

// Metric Card Component
interface MetricCardProps {
  label: string;
  value: string | number;
  icon: JSX.Element;
  highlight?: boolean;
}

const MetricCard: Component<MetricCardProps> = (props) => {
  return (
    <div
      class={`bg-gray-800/50 rounded-xl p-5 border transition-all ${
        props.highlight ? 'border-green-500/50 shadow-lg shadow-green-500/10' : 'border-gray-700'
      }`}
    >
      <div class="flex items-start justify-between">
        <div>
          <p class="text-sm text-gray-400 mb-1">{props.label}</p>
          <p class="text-2xl font-semibold text-white">{props.value}</p>
        </div>
        {props.icon}
      </div>
    </div>
  );
};

export default Dashboard;
